import { useCallback, useEffect, useState, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api } from "./api.js";
import { TenantStatusPanel, actionsFor, type StatusAction } from "./tenants/StatusActions.js";

// Z02 Tenants (S-041; spec 4.3 Z02, TEN-01, H03). The list shows status, modules, credit balance
// and last activity; the onboarding form takes the S03 fields plus price group and referral code.
// The server decides everything (ENT-06): this screen only shows what /v1/zd/tenants returns and
// the reasons it gives. Entered data is kept after an error (spec 4.3).

export interface TenantRow {
  id: string;
  name: string;
  slug: string;
  status: "pending_verification" | "active" | "suspended" | "offboarding" | "closed";
  /** S-014: each module's entitlement status, as the server recorded it (ENT-01). */
  modules: Record<ModuleCode, EntitlementStatus>;
  creditBalance: number;
  lastActivityAt: string;
  createdAt: string;
  referralCode: string | null;
  priceGroup: string | null;
  /** The tenant version, sent as If-Match with a status action (S-042). */
  version: number;
  verifiedAt: string | null;
  suspendedAt: string | null;
}

export type ModuleCode = "lite" | "remembrance";
export type EntitlementStatus = "inactive" | "trial" | "active" | "past_due" | "suspended" | "cancelled";

const MODULE_KEY: Record<ModuleCode, PhraseKey> = {
  lite: "entitlement.module.lite",
  remembrance: "entitlement.module.remembrance",
};

const ENTITLEMENT_KEY: Record<EntitlementStatus, PhraseKey> = {
  inactive: "entitlement.status.inactive",
  trial: "entitlement.status.trial",
  active: "entitlement.status.active",
  past_due: "entitlement.status.past_due",
  suspended: "entitlement.status.suspended",
  cancelled: "entitlement.status.cancelled",
};

const STATUS_KEY: Record<TenantRow["status"], PhraseKey> = {
  pending_verification: "console.tenants.status_pending_verification",
  active: "console.tenants.status_active",
  suspended: "console.tenants.status_suspended",
  offboarding: "console.tenants.status_offboarding",
  closed: "console.tenants.status_closed",
};

type Field =
  | "name"
  | "slug"
  | "colourPrimary"
  | "colourSecondary"
  | "address"
  | "mainPhone"
  | "emergencyPhone"
  | "email"
  | "whatsappNumber"
  | "referralCode"
  | "priceGroup";
type Form = Record<Field, string>;

const EMPTY: Form = {
  name: "",
  slug: "",
  colourPrimary: "#1f3a5f",
  colourSecondary: "#5a6b7d",
  address: "",
  mainPhone: "",
  emergencyPhone: "",
  email: "",
  whatsappNumber: "",
  referralCode: "",
  priceGroup: "",
};

/** A slug suggested from the trading name until the slug is edited by hand (TEN-01 format). */
export function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

const newKey = () => `z02-create-${crypto.randomUUID()}`;
const dateTime = new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Johannesburg" });

function TextField(props: {
  field: Field;
  label: PhraseKey;
  hint?: PhraseKey;
  type?: string;
  maxLength: number;
  form: Form;
  errors: Record<string, string>;
  onChange: (f: Field, v: string) => void;
}) {
  const { field, form, errors } = props;
  const hintId = props.hint ? `${field}-hint` : undefined;
  return (
    <div className="field">
      <label>
        {t(props.label)}
        <input
          name={field}
          type={props.type ?? "text"}
          maxLength={props.maxLength}
          value={form[field]}
          onChange={(e) => props.onChange(field, e.target.value)}
          aria-invalid={!!errors[field]}
          aria-describedby={hintId}
        />
      </label>
      {props.hint && (
        <p className="hint" id={hintId}>
          {t(props.hint)}
        </p>
      )}
      {errors[field] && <p className="field-error">{errors[field]}</p>}
    </div>
  );
}

function NewTenantForm({ onCreated, onCancel }: { onCreated: (name: string, warnings: string[]) => void; onCancel: () => void }) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // One key per creation attempt, kept only across a lost connection, so a retry after a network
  // failure cannot create the tenant twice (NFR-16).
  const [key, setKey] = useState(newKey);

  function change(f: Field, v: string) {
    setForm((prev) => {
      const next = { ...prev, [f]: v };
      if (f === "name" && !slugEdited) next.slug = suggestSlug(v);
      return next;
    });
    if (f === "slug") setSlugEdited(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, priceGroup: form.priceGroup || null };
    const r = await api<{ tenant: { name: string }; warnings: Array<{ field: string; message: string }> }>("/v1/zd/tenants", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": key },
    });
    setBusy(false);
    if (r.status !== 0) setKey(newKey());
    if (r.ok) return onCreated(r.data.tenant.name, r.data.warnings.map((w) => w.message));
    setErrors(r.error?.fields ?? {});
    setError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("common.errors.validation")));
  }

  const common = { form, errors, onChange: change };
  return (
    <form onSubmit={submit} noValidate aria-label={t("console.tenants.form_title")} className="z02-form">
      <h2>{t("console.tenants.form_title")}</h2>
      <p>{t("console.tenants.form_intro")}</p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <TextField field="name" label="console.tenants.label_name" maxLength={100} {...common} />
      <TextField field="slug" label="console.tenants.label_slug" hint="console.tenants.hint_slug" maxLength={40} {...common} />
      <TextField field="colourPrimary" label="console.tenants.label_colour_primary" hint="console.tenants.hint_colour" maxLength={7} {...common} />
      <TextField field="colourSecondary" label="console.tenants.label_colour_secondary" hint="console.tenants.hint_colour" maxLength={7} {...common} />
      <TextField field="address" label="console.tenants.label_address" maxLength={200} {...common} />
      <TextField field="mainPhone" label="console.tenants.label_main_phone" type="tel" maxLength={20} {...common} />
      <TextField field="emergencyPhone" label="console.tenants.label_emergency_phone" hint="console.tenants.hint_optional" type="tel" maxLength={20} {...common} />
      <TextField field="email" label="console.tenants.label_email" type="email" maxLength={254} {...common} />
      <TextField field="whatsappNumber" label="console.tenants.label_whatsapp" hint="console.tenants.hint_whatsapp" type="tel" maxLength={20} {...common} />
      <TextField field="referralCode" label="console.tenants.label_referral" hint="console.tenants.hint_optional" maxLength={40} {...common} />
      <div className="field">
        <label>
          {t("console.tenants.label_price_group")}
          <select name="priceGroup" value={form.priceGroup} onChange={(e) => change("priceGroup", e.target.value)} aria-invalid={!!errors.priceGroup}>
            <option value="">{t("console.tenants.price_group_none")}</option>
            <option value="A">{t("console.tenants.price_group_a")}</option>
            <option value="B">{t("console.tenants.price_group_b")}</option>
            <option value="C">{t("console.tenants.price_group_c")}</option>
          </select>
        </label>
        {errors.priceGroup && <p className="field-error">{errors.priceGroup}</p>}
      </div>
      <p className="hint">{t("console.tenants.hint_logo")}</p>
      <button type="submit" disabled={busy}>
        {busy ? t("console.tenants.creating") : t("console.tenants.create")}
      </button>
      <button type="button" onClick={onCancel} disabled={busy}>
        {t("console.tenants.cancel")}
      </button>
    </form>
  );
}

export function Tenants({ role }: { role?: "support" | "super_admin" } = {}) {
  const [tenants, setTenants] = useState<TenantRow[] | undefined>();
  // S-042: the status action open in the panel under the list (Z03 detail is S-046).
  const [acting, setActing] = useState<{ tenant: TenantRow; action: StatusAction } | undefined>();
  const [statusNotice, setStatusNotice] = useState<string | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<{ name: string; warnings: string[] } | undefined>();

  const load = useCallback(async () => {
    const r = await api<{ tenants: TenantRow[] }>("/v1/zd/tenants");
    if (r.ok) {
      setTenants(r.data.tenants);
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("console.tenants.load_failed")));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="z02" aria-labelledby="z02-title">
      <h2 id="z02-title">{t("console.tenants.title")}</h2>
      {notice && (
        <div role="status" className="notice">
          <p>
            {t("console.tenants.created")} <strong>{notice.name}</strong>
          </p>
          <p>{t("console.tenants.created_next")}</p>
          {notice.warnings.map((w, i) => (
            <p key={i} className="warning">
              {w}
            </p>
          ))}
        </div>
      )}
      {statusNotice && (
        <p role="status" className="notice">
          {statusNotice}
        </p>
      )}
      {acting && (
        <TenantStatusPanel
          key={`${acting.tenant.id}-${acting.action}`}
          tenant={acting.tenant}
          action={acting.action}
          onClose={() => setActing(undefined)}
          onReload={() => void load()}
          onDone={(message) => {
            setActing(undefined);
            setStatusNotice(message);
            void load();
          }}
        />
      )}
      {creating ? (
        <NewTenantForm
          onCancel={() => setCreating(false)}
          onCreated={(name, warnings) => {
            setCreating(false);
            setNotice({ name, warnings });
            void load();
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setNotice(undefined);
            setCreating(true);
          }}
        >
          {t("console.tenants.new_tenant")}
        </button>
      )}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {tenants === undefined && !loadError && <p aria-busy="true">{t("console.tenants.loading")}</p>}
      {tenants && tenants.length === 0 && <p>{t("console.tenants.empty")}</p>}
      {tenants && tenants.length > 0 && (
        <table>
          <thead>
            <tr>
              <th scope="col">{t("console.tenants.column_name")}</th>
              <th scope="col">{t("console.tenants.column_slug")}</th>
              <th scope="col">{t("console.tenants.column_status")}</th>
              <th scope="col">{t("console.tenants.column_modules")}</th>
              <th scope="col">{t("console.tenants.column_balance")}</th>
              <th scope="col">{t("console.tenants.column_last_activity")}</th>
              <th scope="col">{t("console.tenants.status.column_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.slug}</td>
                <td>{t(STATUS_KEY[row.status])}</td>
                <td>
                  <ul className="modules">
                    {(["lite", "remembrance"] as const).map((m) => (
                      <li key={m} data-module={m}>
                        <span>{t(MODULE_KEY[m])}</span> <span>{t(ENTITLEMENT_KEY[row.modules[m]])}</span>
                      </li>
                    ))}
                  </ul>
                </td>
                <td>{row.creditBalance}</td>
                <td>{dateTime.format(new Date(row.lastActivityAt))}</td>
                <td>
                  {actionsFor(row.status, role).map((a) => (
                    <button
                      key={a.action}
                      type="button"
                      aria-label={`${t(a.label)} ${row.name}`}
                      onClick={() => {
                        setStatusNotice(undefined);
                        setActing({ tenant: row, action: a.action });
                      }}
                    >
                      {t(a.label)}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
